import React from "react";
import { Box, styled } from "@acko-tech/building-blocks.ui.common";

/** Bike journey V2 — step badges and active number (design token). */
const VIOLET_SOLID = "#7c47e1";
/** Completed step: solid purple circle with white check (as per V2 reference). */
const COMPLETE_BADGE_BG = VIOLET_SOLID;
/** Active row highlight — design token Purple-P50. */
const ACTIVE_ROW_BG = "var(--Purple-P50, #EFE9FB)";
/** Nav surface — transparent (let page background show). */
const NAV_SURFACE = "transparent";
/** Pending step: pale greyish-lavender circle. */
const PENDING_BADGE_BG = "#efedf5";
const TEXT_DONE = "#1e1a3c";
const TEXT_CURRENT = "#1e1a3c";
const TEXT_PENDING = "#7c7a90";
const PENDING_NUMBER = "#8b879e";
const BORDER_PENDING = "#e4e2ee";

const NavRoot = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  padding: 4px 0;
  border-radius: 14px;
  background: ${NAV_SURFACE};
`;

const StepRow = styled(Box)<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: ${p => (p.$active ? "14px 16px" : "12px 14px")};
  border-radius: 12px;
  background: ${p => (p.$active ? ACTIVE_ROW_BG : "transparent")};
  min-height: ${p => (p.$active ? "52px" : "48px")};
  box-sizing: border-box;
  transition: background 0.15s ease;
`;

const StepBadge = styled.span<{
  $variant: "complete" | "current" | "pending";
}>`
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: -0.02em;
  box-sizing: border-box;
  font-family: inherit;
  border: ${p => {
    if (p.$variant === "pending") return `1.5px solid ${BORDER_PENDING}`;
    if (p.$variant === "complete") return "none";
    return "none";
  }};
  background: ${p => {
    if (p.$variant === "complete") return COMPLETE_BADGE_BG;
    if (p.$variant === "current") return VIOLET_SOLID;
    return PENDING_BADGE_BG;
  }};
  color: ${p => {
    if (p.$variant === "pending") return PENDING_NUMBER;
    if (p.$variant === "current") return "#ffffff";
    return "#ffffff";
  }};
  box-shadow: ${p => {
    if (p.$variant === "current")
      return "0 1px 5px rgba(124, 71, 225, 0.38)";
    if (p.$variant === "complete") return "0 1px 5px rgba(124, 71, 225, 0.26)";
    return "0 1px 2px rgba(30, 26, 60, 0.05)";
  }};
`;

const StepLabel = styled.span<{ $state: "done" | "current" | "pending" }>`
  font-size: ${p => (p.$state === "pending" ? "14px" : "15px")};
  font-weight: ${p => {
    if (p.$state === "pending") return 400;
    if (p.$state === "current") return 600;
    return 700;
  }};
  color: ${p => {
    if (p.$state === "pending") return TEXT_PENDING;
    if (p.$state === "current") return TEXT_CURRENT;
    return TEXT_DONE;
  }};
  line-height: 1.35;
  letter-spacing: ${p => (p.$state === "pending" ? "0" : "-0.01em")};
  font-family: inherit;
`;

const STEPS = [
  { id: "bike_details" as const, label: "Bike details" },
  { id: "plan_addons" as const, label: "Plan & Add ons" },
  { id: "review_pay" as const, label: "Review & Pay" }
];

export type BikeTwoPageJourneyActiveStep =
  | "bike_details"
  | "plan_addons"
  | "review_pay";

export type BikeTwoPageJourneyProgressNavProps = {
  activeStep: BikeTwoPageJourneyActiveStep;
};

/**
 * Desktop left nav for the 2-page bike journey (V2 only).
 */
const BikeTwoPageJourneyProgressNav: React.FC<
  BikeTwoPageJourneyProgressNavProps
> = ({ activeStep }) => {
  const activeIndex = STEPS.findIndex(s => s.id === activeStep);

  return (
    <NavRoot role="navigation" aria-label="Bike insurance steps">
      {STEPS.map((step, index) => {
        const isComplete = index < activeIndex;
        const isCurrent = index === activeIndex;
        const variant = isComplete
          ? "complete"
          : isCurrent
            ? "current"
            : "pending";

        const labelState = isComplete
          ? "done"
          : isCurrent
            ? "current"
            : "pending";

        return (
          <StepRow key={step.id} $active={isCurrent}>
            <StepBadge $variant={variant} aria-hidden>
              {isComplete ? (
                <svg
                  width="13"
                  height="10"
                  viewBox="0 0 14 11"
                  fill="none"
                  aria-hidden
                >
                  <path
                    d="M1 5.5L5 9.5L13 1"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : (
                <span>{index + 1}</span>
              )}
            </StepBadge>
            <StepLabel $state={labelState}>{step.label}</StepLabel>
          </StepRow>
        );
      })}
    </NavRoot>
  );
};

export default BikeTwoPageJourneyProgressNav;
